<template>
  <component
    :is="mode === 'onboarding' ? ConfigPageShell : 'div'"
    v-bind="mode === 'onboarding' ? onboardingShellProps : undefined"
  >
    <template v-if="mode === 'onboarding'" #actions>
      <v-btn
        color="primary"
        variant="flat"
        :disabled="isSaving || (requireSelection && !hasAnyAssignment)"
        :loading="isSaving"
        data-testid="feed-mapping-next"
        @click="saveAndContinue"
      >
        Next
      </v-btn>
    </template>

    <v-alert type="info" variant="tonal" class="mb-4">
      Assign which <strong>feed source</strong> provides authoritative data for each vehicle condition and inventory
      state. Vehicles not present in the designated feed will not be published.
    </v-alert>

    <v-alert v-if="autoSelectedFeedLabel" type="success" variant="tonal" class="mb-4">
      <strong>{{ autoSelectedFeedLabel }}</strong> was selected automatically because it is this account's only
      inventory feed. Review the assignments, then continue.
    </v-alert>

    <v-alert v-if="error" type="error" variant="tonal" class="mb-4">
      {{ error }}
    </v-alert>

    <v-alert v-for="(w, i) in warnings" :key="i" type="warning" variant="tonal" class="mb-2">
      {{ w }}
    </v-alert>

    <v-skeleton-loader v-if="isLoading" type="table" class="mb-4" />

    <template v-if="!isLoading && feedOptions.length > 0">
      <div v-if="feedOptions.length > 1" class="d-flex align-center ga-2 mb-4">
        <v-select
          v-model="applyAllFeed"
          :items="feedSelectItems"
          item-title="label"
          item-value="id"
          density="compact"
          variant="outlined"
          hide-details
          clearable
          label="Feed source"
          :disabled="isSaving"
          style="max-width: 280px"
        />
        <v-btn
          variant="outlined"
          color="primary"
          :disabled="isSaving || !applyAllFeed"
          @click="applySelectedFeedToAll"
        >
          Apply to all states
        </v-btn>
      </div>

      <v-tabs v-model="activeCondition" color="primary" class="mb-4">
        <v-tab v-for="cond in conditions" :key="cond" :value="cond">
          {{ conditionLabel(cond) }}
        </v-tab>
      </v-tabs>

      <v-table density="comfortable">
        <thead>
          <tr>
            <th class="text-left" style="width: 36%">State</th>
            <th class="text-left" style="width: 36%">Feed Source</th>
            <th class="text-left">Exceptions</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="state in vehicleStates" :key="state">
            <!-- Main state row -->
            <tr>
              <td class="text-body-2 font-weight-medium">
                {{ stateLabel(state) }}
              </td>
              <td>
                <v-select
                  :model-value="getAssignment(activeCondition, state)"
                  @update:model-value="(val: string | null) => setAssignment(activeCondition, state, val)"
                  :items="feedSelectItems"
                  item-title="label"
                  item-value="id"
                  density="compact"
                  variant="outlined"
                  hide-details
                  :disabled="isSaving"
                  clearable
                  placeholder="— None —"
                  class="feed-select"
                />
              </td>
              <td>
                <div class="d-flex align-center" style="gap: 4px;">
                  <!-- Badge showing how many overrides/fallback exist -->
                  <v-chip
                    v-if="stateExceptionCount(activeCondition, state) > 0"
                    size="x-small"
                    color="primary"
                    variant="tonal"
                    class="mr-1"
                  >
                    {{ stateExceptionCount(activeCondition, state) }}
                  </v-chip>
                  <v-btn
                    variant="text"
                    size="small"
                    :disabled="isSaving || !getAssignment(activeCondition, state)"
                    :color="isExpanded(activeCondition, state) ? 'primary' : 'default'"
                    :append-icon="isExpanded(activeCondition, state) ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                    style="text-transform: none; font-size: 0.75rem; letter-spacing: 0"
                    @click="toggleExpanded(activeCondition, state)"
                  >
                    {{ isExpanded(activeCondition, state) ? 'Hide' : 'Exceptions' }}
                  </v-btn>
                </div>
              </td>
            </tr>

            <!-- Expanded exceptions panel -->
            <template v-if="isExpanded(activeCondition, state)">
              <!-- Fallback source row -->
              <tr class="override-row">
                <td colspan="3" class="py-2 px-6">
                  <div class="fallback-row-inner">
                    <span class="fallback-label-text">Fallback source</span>
                    <v-select
                      :model-value="getFallback(activeCondition, state)"
                      @update:model-value="(val: string | null) => setFallback(activeCondition as FeedMappingCondition, state, val)"
                      :items="fallbackFeedItems(activeCondition, state)"
                      item-title="label"
                      item-value="id"
                      density="compact"
                      variant="outlined"
                      hide-details
                      :disabled="isSaving"
                      clearable
                      placeholder="— None —"
                      style="max-width: 200px"
                    />
                    <span class="fallback-hint">Used when vehicle is not in the primary feed</span>
                  </div>
                </td>
              </tr>

              <!-- Attribute-based override rows -->
              <tr
                v-for="(ov, ovIdx) in getOverridesForSlot(activeCondition, state)"
                :key="`ov-${ovIdx}`"
                class="override-row"
              >
                <td colspan="3" class="py-2 px-6">
                  <div class="override-row-inner">
                    <span class="override-label">If</span>

                    <v-combobox
                      :model-value="ov.filter.field"
                      @update:model-value="(val: unknown) => updateOverride(activeCondition, state, ovIdx, 'field', resolveFieldSelection(val))"
                      :items="srpFields"
                      item-title="label"
                      item-value="value"
                      density="compact"
                      variant="outlined"
                      hide-details
                      placeholder="Field"
                      :disabled="isSaving"
                      style="width: 220px; flex: 0 0 220px"
                    />

                    <v-select
                      :model-value="ov.filter.operator"
                      @update:model-value="(val: OverrideOperator) => updateOverride(activeCondition, state, ovIdx, 'operator', val)"
                      :items="operatorItems"
                      item-title="label"
                      item-value="value"
                      density="compact"
                      variant="outlined"
                      hide-details
                      :disabled="isSaving"
                      style="width: 160px; flex: 0 0 160px"
                    />

                    <v-text-field
                      v-if="!isValuelessOperator(ov.filter.operator)"
                      :model-value="ov.filter.value"
                      @update:model-value="(val: string) => updateOverride(activeCondition, state, ovIdx, 'value', val)"
                      density="compact"
                      variant="outlined"
                      hide-details
                      placeholder="value  (e.g. 1)"
                      :disabled="isSaving"
                      style="width: 130px; flex: 0 0 130px"
                    />

                    <span class="override-arrow">use</span>

                    <v-select
                      :model-value="ov.feed"
                      @update:model-value="(val: string) => updateOverride(activeCondition, state, ovIdx, 'feed', val)"
                      :items="feedSelectItems"
                      item-title="label"
                      item-value="id"
                      density="compact"
                      variant="outlined"
                      hide-details
                      :disabled="isSaving"
                      placeholder="Select feed"
                      style="width: 180px; flex: 0 0 180px"
                    />

                    <v-btn
                      icon="mdi-delete-outline"
                      variant="text"
                      size="small"
                      color="error"
                      :disabled="isSaving"
                      @click="removeOverride(activeCondition, state, ovIdx)"
                    />
                  </div>
                </td>
              </tr>

              <!-- Add exception button -->
              <tr class="override-row">
                <td colspan="3" class="pt-0 pb-2 px-6">
                  <v-btn
                    variant="text"
                    size="small"
                    prepend-icon="mdi-plus"
                    color="primary"
                    :disabled="isSaving"
                    style="text-transform: none; font-size: 0.75rem; letter-spacing: 0"
                    @click="addOverride(activeCondition, state)"
                  >
                    Add Exception
                  </v-btn>
                </td>
              </tr>
            </template>
          </template>
        </tbody>
      </v-table>
    </template>

    <div v-if="mode !== 'onboarding'" class="d-flex justify-end mt-4">
      <v-btn
        color="primary"
        variant="flat"
        :disabled="isSaving || !isDirty"
        :loading="isSaving"
        data-testid="feed-mapping-save"
        @click="saveOnly"
      >
        Save
      </v-btn>
    </div>
  </component>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useJazelAppConfig } from '@jazel/jazel-vuetify'
import ConfigPageShell from '@/components/config/ConfigPageShell.vue'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { usePublishStore } from '@/stores/usePublishStore'
import { fetchIseMappingFields } from '@/services/fieldMappingService'
import { fetchFeedMapping, saveFeedMapping } from '@/services/feedMappingService'
import { buildAllStateFeedMapping } from '@/utils/feedMappingDefaults'
import {
  VALID_CONDITIONS,
  VEHICLE_STATES,
  VEHICLE_STATE_LABELS,
  OVERRIDE_OPERATORS,
  OVERRIDE_OPERATOR_LABELS,
  type FeedMapping,
  type FeedMappingCondition,
  type FeedMappingOverride,
  type FeedMappingFallback,
  type OverrideOperator,
} from '@/types/feedMapping'

type Mode = 'onboarding' | 'settings'
type FeedOption = { id: string; label: string }

const props = withDefaults(
  defineProps<{
    mode?: Mode
    requireSelection?: boolean
  }>(),
  {
    mode: 'settings',
    requireSelection: false,
  },
)

const emit = defineEmits<{
  (e: 'saved', value: { derivedPrimarySources: string[] }): void
  (e: 'continue'): void
}>()

const onboardingShellProps = {
  title: 'Feed Source Mapping',
  subtitle: 'Choose which feed source is authoritative for each inventory state.',
  showBackToSetup: false,
  dense: true,
} as const

const appConfig = useJazelAppConfig()
const accountSettings = useAccountSettingsStore()
const publishStore = usePublishStore()

const isLoading = ref(false)
const isSaving = ref(false)
const error = ref('')
const warnings = ref<string[]>([])
const feedOptions = ref<FeedOption[]>([])
const srpFields = ref<{ value: string; label: string }[]>([])
const autoSelectedFeedLabel = ref('')
const applyAllFeed = ref<string | null>(null)

const conditions = VALID_CONDITIONS
const vehicleStates = VEHICLE_STATES
const activeCondition = ref<FeedMappingCondition>('new')

// Matrix state
const mapping: Record<string, Record<string, string | null>> = reactive({})
const persistedMapping = ref<FeedMapping | null>(null)

// Overrides state
const overrides = ref<FeedMappingOverride[]>([])
const persistedOverrides = ref<FeedMappingOverride[]>([])

// Fallback sources state
const fallbacks = ref<FeedMappingFallback[]>([])
const persistedFallbacks = ref<FeedMappingFallback[]>([])

// Tracks which (condition|state) rows are expanded to show overrides
const expandedSlots = reactive<Record<string, boolean>>({})

// ---------------------------------------------------------------------------
// Override helpers
// ---------------------------------------------------------------------------

function slotKey(condition: string, state: string): string {
  return `${condition}|${state}`
}

function isExpanded(condition: string, state: string): boolean {
  return !!expandedSlots[slotKey(condition, state)]
}

function toggleExpanded(condition: string, state: string): void {
  const key = slotKey(condition, state)
  expandedSlots[key] = !expandedSlots[key]
}

function getOverridesForSlot(condition: string, state: string): FeedMappingOverride[] {
  return overrides.value.filter(
    (ov) => ov.condition === condition && ov.state === state,
  )
}

function stateExceptionCount(condition: string, state: string): number {
  const overrideCount = getOverridesForSlot(condition, state).length
  const hasFallback = getFallback(condition, state) ? 1 : 0
  return overrideCount + hasFallback
}

function addOverride(condition: FeedMappingCondition, state: string): void {
  overrides.value.push({
    condition,
    state,
    filter: { field: '', operator: 'equals', value: '' },
    feed: '',
  })
}

function removeOverride(condition: string, state: string, localIdx: number): void {
  const slotOverrides = getOverridesForSlot(condition, state)
  const target = slotOverrides[localIdx]
  if (!target) return
  const globalIdx = overrides.value.indexOf(target)
  if (globalIdx !== -1) overrides.value.splice(globalIdx, 1)
}

function updateOverride(
  condition: string,
  state: string,
  localIdx: number,
  field: 'field' | 'operator' | 'value' | 'feed',
  value: string,
): void {
  const slotOverrides = getOverridesForSlot(condition, state)
  const target = slotOverrides[localIdx]
  if (!target) return
  if (field === 'feed') {
    target.feed = value
  } else {
    ;(target.filter as any)[field] = value
  }
}

function isValuelessOperator(op: OverrideOperator): boolean {
  return op === 'missing' || op === 'not_missing'
}

/** Resolves a v-combobox value which may be a plain string or a { value, label } item object. */
function resolveFieldSelection(val: unknown): string {
  if (val === null || val === undefined) return ''
  if (typeof val === 'object') return String((val as any).value ?? '')
  return String(val)
}

const operatorItems = computed(() =>
  OVERRIDE_OPERATORS.map((op) => ({ value: op, label: OVERRIDE_OPERATOR_LABELS[op] })),
)

// ---------------------------------------------------------------------------
// Fallback helpers
// ---------------------------------------------------------------------------

function getFallback(condition: string, state: string): string | null {
  const fb = fallbacks.value.find(
    (f) => f.condition === condition && f.state === state,
  )
  return fb?.feed ?? null
}

function setFallback(condition: FeedMappingCondition, state: string, feed: string | null) {
  const idx = fallbacks.value.findIndex(
    (f) => f.condition === condition && f.state === state,
  )
  if (feed) {
    if (idx !== -1) {
      fallbacks.value[idx].feed = feed
    } else {
      fallbacks.value.push({ condition, state, feed })
    }
  } else {
    if (idx !== -1) fallbacks.value.splice(idx, 1)
  }
}

/** Fallback feed options: exclude the primary feed for this slot */
function fallbackFeedItems(condition: string, state: string) {
  const primary = getAssignment(condition, state)
  return feedSelectItems.value.filter((f) => f.id !== primary)
}

// ---------------------------------------------------------------------------
// Matrix helpers
// ---------------------------------------------------------------------------

function conditionLabel(cond: string): string {
  return cond.charAt(0).toUpperCase() + cond.slice(1)
}

function stateLabel(state: string): string {
  return VEHICLE_STATE_LABELS[state] ?? state
}

const feedSelectItems = computed(() => {
  return feedOptions.value.map((f) => ({ id: f.id, label: f.label }))
})

function getAssignment(condition: string, state: string): string | null {
  return mapping[condition]?.[state] ?? null
}

function setAssignment(condition: string, state: string, feed: string | null) {
  if (!mapping[condition]) mapping[condition] = {}
  mapping[condition][state] = feed || null

  // When the default feed is cleared, collapse and remove its overrides + fallback
  if (!feed) {
    expandedSlots[slotKey(condition, state)] = false
    overrides.value = overrides.value.filter(
      (ov) => !(ov.condition === condition && ov.state === state),
    )
    fallbacks.value = fallbacks.value.filter(
      (fb) => !(fb.condition === condition && fb.state === state),
    )
  }

  // If primary feed is changed and matches the fallback, clear the fallback
  if (feed) {
    const fbIdx = fallbacks.value.findIndex(
      (fb) => fb.condition === condition && fb.state === state && fb.feed === feed,
    )
    if (fbIdx !== -1) fallbacks.value.splice(fbIdx, 1)
  }
}

function applyFeedToAllStates(feed: string): void {
  applyMapping(buildAllStateFeedMapping(feed))
}

function applySelectedFeedToAll(): void {
  if (!applyAllFeed.value) return
  applyFeedToAllStates(applyAllFeed.value)
}

const hasAnyAssignment = computed(() => {
  for (const cond of Object.values(mapping)) {
    for (const feed of Object.values(cond)) {
      if (feed) return true
    }
  }
  return false
})

const isDirty = computed(() => {
  return (
    JSON.stringify(buildPayload()) !== JSON.stringify(persistedMapping.value ?? {}) ||
    JSON.stringify(overrides.value) !== JSON.stringify(persistedOverrides.value) ||
    JSON.stringify(fallbacks.value) !== JSON.stringify(persistedFallbacks.value)
  )
})

function buildPayload(): FeedMapping {
  const result: FeedMapping = {}
  for (const cond of conditions) {
    const stateMap = mapping[cond]
    if (!stateMap) continue
    const filtered: Record<string, string | null> = {}
    let hasEntries = false
    for (const [state, feed] of Object.entries(stateMap)) {
      if (feed !== null && feed !== undefined) {
        filtered[state] = feed
        hasEntries = true
      }
    }
    if (hasEntries) result[cond] = filtered
  }
  return result
}

/** Only include overrides that are fully filled in (feed + field). */
function buildOverridesPayload(): FeedMappingOverride[] {
  return overrides.value.filter(
    (ov) => ov.feed && ov.filter.field && ov.filter.operator,
  )
}

const FEED_LABEL_MAP: Record<string, string> = {
  vauto: 'VAuto',
  fdds1: 'FDDS1',
  fdds2: 'FDDS2',
  homenet: 'HomeNet',
  homenet_api: 'HomeNet API',
  locate: 'Locate',
  icc: 'ICC',
  idms: 'IDMS',
  dealerslink: 'DealersLink',
}

function formatFeedLabel(importId: string): string {
  const raw = String(importId || '').trim()
  const base = raw.startsWith('import_') ? raw.slice('import_'.length) : raw
  if (FEED_LABEL_MAP[base]) return FEED_LABEL_MAP[base]
  return base.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function resolveIseEndpoint(): string {
  const cfg = (appConfig as any) || {}
  return String(cfg?.iseBackendUrl || import.meta.env.VITE_ISE_BACKEND_URL || '').replace(/\/+$/g, '')
}

function applyMapping(fm: FeedMapping | null) {
  for (const cond of conditions) {
    mapping[cond] = {}
  }
  if (!fm) return
  for (const [cond, stateMap] of Object.entries(fm)) {
    if (!mapping[cond]) mapping[cond] = {}
    for (const [state, feed] of Object.entries(stateMap)) {
      mapping[cond][state] = feed
    }
  }
}

async function loadData() {
  error.value = ''
  warnings.value = []
  feedOptions.value = []
  autoSelectedFeedLabel.value = ''
  applyAllFeed.value = null
  isLoading.value = true

  const iseEndpoint = resolveIseEndpoint()
  const accountId = String(accountSettings.accountId || '').trim()
  const token = String(accountSettings.sessionToken || '').trim()

  if (!iseEndpoint || !accountId) {
    error.value = 'Missing ISE endpoint or account id.'
    isLoading.value = false
    return
  }

  try {
    const [fieldsResp, mappingResp] = await Promise.all([
      fetchIseMappingFields(iseEndpoint, accountId),
      fetchFeedMapping(iseEndpoint, accountId, { token }).catch(() => null),
    ])

    const details = fieldsResp?.feedDetails || []
    feedOptions.value = details
      .filter((d: any) => d?.type === 'feed' && typeof d?.id === 'string' && d.id.startsWith('import_'))
      .map((d: any) => ({
        id: d.id.replace(/^import_/, ''),
        label: formatFeedLabel(d.id),
      }))

    const fieldData: Array<{ key?: string } | string> = Array.isArray(fieldsResp?.data)
      ? (fieldsResp!.data as any[])
      : []
    srpFields.value = fieldData
      .map((f) => (typeof f === 'string' ? f : (f as any)?.key ?? ''))
      .filter(Boolean)
      .sort()
      .map((key: string) => ({
        value: key,
        label: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      }))

    if (feedOptions.value.length === 0) {
      error.value =
        'No import feeds are configured for this dealer. Add an import_* product in JAMS, then refresh.'
    }

    const fm = mappingResp?.feedMapping ?? null
    persistedMapping.value = fm ? JSON.parse(JSON.stringify(fm)) : null
    if (fm) {
      applyMapping(fm)
    } else if (feedOptions.value.length === 1) {
      const onlyFeed = feedOptions.value[0]
      applyFeedToAllStates(onlyFeed.id)
      autoSelectedFeedLabel.value = onlyFeed.label
    } else {
      applyMapping(null)
    }

    const loadedOverrides: FeedMappingOverride[] = Array.isArray(mappingResp?.overrides)
      ? mappingResp!.overrides
      : []
    overrides.value = JSON.parse(JSON.stringify(loadedOverrides))
    persistedOverrides.value = JSON.parse(JSON.stringify(loadedOverrides))

    const loadedFallbacks: FeedMappingFallback[] = Array.isArray(mappingResp?.fallbacks)
      ? mappingResp!.fallbacks
      : []
    fallbacks.value = JSON.parse(JSON.stringify(loadedFallbacks))
    persistedFallbacks.value = JSON.parse(JSON.stringify(loadedFallbacks))
  } catch (e: any) {
    error.value = e?.message || 'Failed to load feed data.'
  } finally {
    isLoading.value = false
  }
}

async function saveInternal(): Promise<boolean> {
  error.value = ''
  warnings.value = []
  if (isSaving.value) return false

  const payload = buildPayload()
  const overridesPayload = buildOverridesPayload()

  if (props.mode === 'onboarding' && props.requireSelection && !hasAnyAssignment.value) {
    error.value = 'Please assign at least one feed to continue.'
    return false
  }

  isSaving.value = true
  try {
    const iseEndpoint = resolveIseEndpoint()
    const accountId = String(accountSettings.accountId || '').trim()
    const token = String(accountSettings.sessionToken || '').trim()
    if (!iseEndpoint || !accountId) {
      error.value = 'Missing ISE endpoint or account id.'
      return false
    }

    const fallbacksPayload = fallbacks.value.filter((fb) => fb.feed)
    const result = await saveFeedMapping(iseEndpoint, accountId, payload, overridesPayload, fallbacksPayload, { token })

    warnings.value = result.warnings ?? []
    const savedMapping = result.effectiveFeedMapping ?? payload
    applyMapping(savedMapping)
    persistedMapping.value = JSON.parse(JSON.stringify(savedMapping))
    persistedOverrides.value = JSON.parse(JSON.stringify(overridesPayload))
    persistedFallbacks.value = JSON.parse(JSON.stringify(fallbacksPayload))
    emit('saved', { derivedPrimarySources: result.derivedPrimarySources })
    await publishStore.refreshUnpublishedChangesSummary()
    return true
  } catch (e: any) {
    error.value = e?.message || 'Failed to save feed mapping.'
    return false
  } finally {
    isSaving.value = false
  }
}

async function saveAndContinue() {
  const ok = await saveInternal()
  if (ok) emit('continue')
}

async function saveOnly() {
  await saveInternal()
}

onMounted(() => {
  void loadData()
})

watch(
  () => props.mode,
  () => {
    void loadData()
  },
)
</script>

<style scoped>
.feed-select {
  max-width: 280px;
}

.override-row td {
  background-color: #f5f5f5;
  border-top: none !important;
}

.fallback-row-inner {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap;
}

.fallback-label-text {
  white-space: nowrap;
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.7);
  font-weight: 500;
}

.fallback-hint {
  white-space: nowrap;
  font-size: 0.7rem;
  color: rgba(0, 0, 0, 0.4);
  font-style: italic;
}

.override-row-inner {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
}

.override-label {
  white-space: nowrap;
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
  font-weight: 500;
  min-width: 16px;
}

.override-arrow {
  white-space: nowrap;
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.38);
  font-weight: 500;
  padding: 0 4px;
  border-left: 2px solid rgba(0, 0, 0, 0.12);
  border-right: 2px solid rgba(0, 0, 0, 0.12);
  margin: 0 2px;
}
</style>
